/**
 * The shop's time zone setting (Configuration > Time zone): what the API returns and how a
 * zone is shown in the picker.
 */

export interface BusinessTimeZoneView {
    /** The zone in force, e.g. `Europe/Bucharest`. */
    timeZone: string;
    defaultTimeZone: string;
    /** The zones that may be chosen. */
    options: string[];
}

export interface TimeZoneOption {
    value: string;
    label: string;
}

/** The zone's offset from UTC at `at`, as `+02:00`. */
export const utcOffsetOf = (timeZone: string, at: Date): string => {
    // `15/01/2026, GMT+02:00`. A zero offset is `GMT+00:00` or `GMT` alone depending on the ICU
    // build, so the empty case is padded rather than branched on: a branch only one of the two
    // builds ever takes cannot be covered on both.
    const formatted = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        timeZoneName: 'longOffset',
    }).format(at);
    const offset = formatted.slice(formatted.lastIndexOf('GMT') + 3);
    return `${offset}+00:00`.slice(0, '+00:00'.length);
};

/** `Europe/Bucharest (UTC+02:00)`, the offset as of `at`. */
export const timeZoneOptionLabel = (timeZone: string, at: Date): string =>
    `${timeZone.split('_').join(' ')} (UTC${utcOffsetOf(timeZone, at)})`;

export const toTimeZoneOptions = (timeZones: readonly string[], at: Date): TimeZoneOption[] =>
    timeZones.map(timeZone => ({ value: timeZone, label: timeZoneOptionLabel(timeZone, at) }));
